import truncate from 'lodash/truncate';
import React, { ComponentPropsWithRef, FC } from 'react';
import { SocialNetwork } from '@actus/types';
import { FacebookLogoIcon } from '@components/icons/social/FacebookLogo.icon';
import { LinkedinLogoIcon } from '@components/icons/social/LinkedinLogo.icon';
import { TikTokLogoIcon } from '@icons/social/TikTokLogo.icon';
import { XLogoIcon } from '@icons/social/XLogo.icon';
import { YoutubeLogoIcon } from '@icons/social/YoutubeLogo.icon';
import { useTranslation } from '@locales/useTranslation.hook';
import { Constants } from '@utils/Constants';
import { isServer } from '@utils/server';
import { PlcNextPageContext } from 'features/business/app.types';

const TWITTER_LENGTH = 280;

export type ShareProps = {
  socialNetwork: SocialNetwork;
  imgUrl?: string;
  imgAlt?: string;
  title?: string;
  // up to 200 chars
  description?: string;
  iconSize?: number;
  url?: string;
};

type SharerProps = Omit<ShareProps, 'socialNetwork'> & { socialNetwork?: ShareProps['socialNetwork']; url: string };

type Props = ComponentPropsWithRef<'button'> & ShareProps;

const openShareWindow = (shareUrl: string | URL | undefined) => {
  const width = 550;
  const height = 400;
  const top = window.innerHeight / 2 - height / 2;
  const left = window.innerWidth / 2 - width / 2;

  window.open(shareUrl, '_blank', `width=${width},height=${height},top=${top},left=${left},resizable,scrollbars`);
};

const buildSocialIcon = ({ socialNetwork, iconSize }: Omit<SharerProps, 'imgUrl'>): JSX.Element | undefined => {
  const socialIconClass = 'plc-mr-xxs plc-mr-up-s-s';
  switch (socialNetwork) {
    case SocialNetwork.Facebook: {
      return (
        <a className={socialIconClass} onClick={() => openShareWindow(Constants.SOCIAL.FACEBOOK_URL)} target="_blank">
          <FacebookLogoIcon size={iconSize} />
        </a>
      );
    }
    case SocialNetwork.X: {
      return (
        <a className={socialIconClass} onClick={() => openShareWindow(Constants.SOCIAL.X_URL)} target="_blank">
          <XLogoIcon size={iconSize} />
        </a>
      );
    }
    case SocialNetwork.Linkedin: {
      return (
        <a className={socialIconClass} onClick={() => openShareWindow(Constants.SOCIAL.LINKEDIN_URL)} target="_blank">
          <LinkedinLogoIcon size={iconSize} />
        </a>
      );
    }
    case SocialNetwork.Youtube: {
      return (
        <a className={socialIconClass} onClick={() => openShareWindow(Constants.SOCIAL.YOUTUBE_URL)} target="_blank">
          <YoutubeLogoIcon size={iconSize} />
        </a>
      );
    }
    case SocialNetwork.TikTok: {
      return (
        <a onClick={() => openShareWindow(Constants.SOCIAL.TIKTOK_URL)} target="_blank">
          <TikTokLogoIcon size={iconSize} />
        </a>
      );
    }
    case undefined:
    default: {
      return socialNetwork;
    }
  }
};

export const buildMetaTags = ({ imgUrl, title, url, description, imgAlt }: SharerProps): JSX.Element => {
  // Twitter : https://developer.twitter.com/en/docs/tweets/optimize-with-cards/overview/markup
  // Facebook : https://developers.facebook.com/tools/debug
  return (
    <>
      <meta key="fb:app_id" content="766998407408723" property="fb:app_id" />
      <meta key="og:type" content="article" property="og:type" />
      {title && <meta key="og:title" content={title} property="og:title" />}

      {imgUrl && <meta key="og:image" content={imgUrl} property="og:image" />}
      {imgUrl && imgAlt && <meta key="og:image:alt" content={imgAlt} property="og:image:alt" />}
      {description && <meta key="og:description" content={description} property="og:description" />}
      {url && <meta key="og:url" content={url} property="og:url" />}

      <meta key="twitter:card" content="summary" name="twitter:card" />
      {title && <meta key="twitter:title" content={title} name="twitter:title" />}
      {imgUrl && <meta key="twitter:image" content={imgUrl} name="twitter:image" />}
      {imgUrl && imgAlt && <meta key="twitter:image:alt" content={imgAlt} name="twitter:image:alt" />}
      {description && (
        <meta
          key="twitter:description"
          content={
            description.length > TWITTER_LENGTH
              ? truncate(description, { length: TWITTER_LENGTH, separator: /,? +/ })
              : description
          }
          name="twitter:description"
        />
      )}
      <meta key="twitter:site" content="@Pleinchamp" name="twitter:site" />
    </>
  );
};

const SocialShareButton: FC<Props> = ({ socialNetwork, title, imgUrl, imgAlt, iconSize, url, ...rest }) => {
  const { t } = useTranslation(['common']);
  if (!url){
    url = isServer() ? '' : window.location.href;
  }

  return <>{buildSocialIcon({ iconSize, socialNetwork, title: title || t('common.app-name'), url, ...rest })}</>;
};

export async function getServerSideProps(ctx: PlcNextPageContext) {
  const { req } = ctx;

  const protocol = req?.headers['x-forwarded-proto'] || 'http';
  const host = req?.headers['host'];
  const url = req?.url;
  const absoluteUrl = `${protocol}://${host}${url}`;

  return {
    props: {
      url: absoluteUrl
    }
  }
}

export { SocialShareButton };
